import { ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { routerPath } from "@/common/router";
import {
  ListMemberDocument,
  ListMemberQuery,
  GetPrefCitySectionByZipCodeDocument,
  GetPrefCitySectionByZipCodeQuery,
} from "@/generated/graphql";
import regularDeliveryStore from "@/store/regularDeliveryStore";
import type { MemberDisplayData, MemberResultItem, DeliveryAvailabilityData } from "../types";

interface UseRegularDeliveryRegistFormParams {
  dialog: Ref<boolean>;
  messagedialog: Ref<boolean>;
  message: Ref<string>;
  wordList: Record<string, string>;
  deliveryAvailabilityStore: Ref<DeliveryAvailabilityData[][]>;
  house_card_display: Ref<number>;
  is_hide_other_shop_members: Ref<boolean | null | undefined>;
  prefListItems: Ref<(string | null | undefined)[] | null | undefined>;
}

/**
 * 登録フォームの入力状態と操作を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useRegularDeliveryRegistForm = ({
  dialog,
  messagedialog,
  message,
  wordList,
  deliveryAvailabilityStore,
  house_card_display,
  is_hide_other_shop_members,
  prefListItems,
}: UseRegularDeliveryRegistFormParams) => {
  const router = useRouter();

  const btnNonMemberToggle = ref<number>(0);
  const prefSelectValue = ref<string>("");
  const phone_number = ref<string>("");
  const user_name = ref<string>("");
  const house_card = ref<string>("");
  const userErrorMessage = ref<string>("");
  const zip_code = ref<string>("");
  const errorZipCode = ref<string>("");
  const errorPrefSelect = ref<string>("");
  const listMemberResult= ref<MemberResultItem[] | null | undefined>([]);
  const memberData = ref<MemberDisplayData[]>([]);

  /**
   * 郵便番号選択 @Click
   */
  const onClickToggleZip = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 0;
  };

  /**
   * 都道府県選択 @Click
   */
  const onClickTogglePref = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 1;
  };

  /**
   * 郵便番号入力イベント @input
   */
  const onZipCodeInput = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    // 7桁の場合検索する、それ以外の場合住所をクリアする
    if (zip_code.value.length != 7) {
      return;
    }
    // 郵便番号が有効かチェックする
    const adddressBlock: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
      await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
        query: GetPrefCitySectionByZipCodeDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          zip_code: zip_code.value,
        },
        fetchPolicy: "no-cache",
      });

    if (
      !adddressBlock.data.getPrefCitySectionByZipCode ||
      adddressBlock.data.getPrefCitySectionByZipCode.length == 0
    ) {
      errorZipCode.value = "入力した郵便番号はご利用対象外です";
      return;
    }
  };
  /**
   * 都道府県入力イベント @input
   */
  const onPrefSelectInput = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
  };

  ////////////////////////////////////////////////////////////////////////////////
  //                                                                  会員登録
  /**
   * 「配達依頼・会員登録」ボタン @Click
   */
  const onClickDeliveryRequestAndNewMember = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 郵便番号が7桁の場合検索する
    if (btnNonMemberToggle.value == 0 && zip_code.value.length !== 7) {
      errorZipCode.value = "郵便番号は7文字でなければなりません。";
      return;
    }
    // 都道府県が選択された場合、検索する
    if (
      btnNonMemberToggle.value == 1 &&
      (prefSelectValue.value == "" || prefSelectValue.value == null)
    ) {
      errorPrefSelect.value = "都道府県が入力されていません。";
      return;
    }

    if (btnNonMemberToggle.value == 0) {
      if (errorZipCode.value !== null && errorZipCode.value.length > 0) {
        return;
      }
      // 数字のみ
      if (!/^\d+$/.test(zip_code.value)) {
        return;
      }
      // 郵便番号から丁目のリストを取得する
      const adddressBlock: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
        await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
          query: GetPrefCitySectionByZipCodeDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            zip_code: zip_code.value,
          },
          fetchPolicy: "no-cache",
        });
      // 丁目のリストがあった場合のみ、次の画面へ遷移するためデータをstoreする
      if (
        adddressBlock.data.getPrefCitySectionByZipCode &&
        adddressBlock.data.getPrefCitySectionByZipCode.length > 0
      ) {
        await regularDeliveryStore.setupValue({
          zip_code: zip_code.value,
          pref_name: "",
          user_id: "",
          areaDialog: false,
          deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
          regular_delivery_no: "",
          processFlag: 0,
        });
      } else {
        // 配達可能な住所がない場合はエラーを表示し画面遷移しない
        errorZipCode.value = "入力した郵便番号はご利用対象外です";
        return;
      }
    } else if (btnNonMemberToggle.value == 1) {
      // 次の画面へ遷移するためデータをstoreする
      await regularDeliveryStore.setupValue({
        zip_code: "",
        pref_name: prefSelectValue.value,
        user_id: "",
        areaDialog: true,
        deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
        regular_delivery_no: "",
        processFlag: 0,
      });
    }
    // 「定期配達登録-入力」画面へ遷移
    await router.push({
      path: routerPath.regularDeliveryRegister,
    });
  };

  ////////////////////////////////////////////////////////////////////////////////
  //                                                                  登録済み会員

  /**
   * 「配達依頼」ボタン @Click
   */
  const onClickDeliveryRequest = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    if (house_card_display.value == 1) {
      // 電話番号が4桁未満や、名前、ハウスカードが入力しない場合
      if (
        phone_number.value.length < 4 &&
        user_name.value.length == 0 &&
        house_card.value.length == 0
      ) {
        userErrorMessage.value =
          "電話番号、お名前、または" +
          wordList["lbl_house_card"] +
          "を入力してください。";
        return;
      }
    } else {
      // 電話番号が4桁未満や、名前が入力しない場合
      if (phone_number.value.length < 4 && user_name.value.length == 0) {
        userErrorMessage.value = "電話番号、またはお名前を入力してください。";
        return;
      }
    }

    /**
     * 電話番号・名前（かな）で会員情報を取得する
     */
    const listMember: ApolloQueryResult<ListMemberQuery> =
      await apolloClient.query<ListMemberQuery>({
        query: ListMemberDocument,
        variables: {
          input: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            section_shop_code: localStorageGetShopCode(),
            is_hide_other_shop_members: is_hide_other_shop_members.value,
            phone: phone_number.value,
            kana: user_name.value,
            house_card: house_card.value,
            limit: 200,
            offset: 0,
            sortBy: "updated_date",
            sortOrder: "desc",
          },
        },
        fetchPolicy: "no-cache",
      });
    // 会員の検索結果を引き取る
    listMemberResult.value = listMember.data.listMember;
    // 結果がない場合
    if (listMemberResult.value == null || listMemberResult.value.length == 0) {
      userErrorMessage.value = "該当条件の会員情報がありません。";
      return;
    }

    // 結果が2件以上、もしくは1件で配達できない住所の会員の場合
    if (
      listMemberResult.value.length > 1 ||
      (listMemberResult.value.length === 1 &&
        listMemberResult.value[0] &&
        listMemberResult.value[0]!.is_deliverable === 0)
    ) {
      memberData.value = [];
      listMemberResult.value.forEach((value) => {
        if (value !== undefined && value !== null) {
          memberData.value.push({
            member_id: value.member_id,
            name: value.name1 + " " + value.name2,
            phone_number: value.phone,
          });
        }
      });
      dialog.value = true;
      return;
    }

    await regularDeliveryStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id:
        listMemberResult.value && listMemberResult.value[0]
          ? listMemberResult.value[0]?.member_id ?? ""
          : "",
      areaDialog: false,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      regular_delivery_no: "",
      processFlag: 0,
    });

    // 「定期配達登録-入力」画面へ遷移
    await router.push({
      path: routerPath.regularDeliveryRegister,
    });
  };

  const onClickDeliveryRequestByUser = async (
    item: MemberResultItem
  ): Promise<void> => {
    if (!item) {
      console.error("無効な会員情報が渡されました。");
      return;
    }
    regularDeliveryStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: item.member_id,
      areaDialog: false,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      regular_delivery_no: "",
      processFlag: 0,
    });
    // 「定期配達登録-入力」画面へ遷移
    router.push({
      path: routerPath.regularDeliveryRegister,
    });
  };

  const onClickDeliveryRequestByNumberId = async (
    user_id: string
  ): Promise<void> => {
    await regularDeliveryStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: user_id,
      areaDialog: false,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      regular_delivery_no: "",
      processFlag: 0,
    });

    // 「定期配達登録-入力」画面へ遷移
    await router.push({ path: routerPath.regularDeliveryRegister });
  };

  return {
    btnNonMemberToggle,
    prefSelectValue,
    phone_number,
    user_name,
    house_card,
    userErrorMessage,
    zip_code,
    errorZipCode,
    errorPrefSelect,
    listMemberResult,
    memberData,
    onClickToggleZip,
    onClickTogglePref,
    onZipCodeInput,
    onPrefSelectInput,
    onClickDeliveryRequestAndNewMember,
    onClickDeliveryRequest,
    onClickDeliveryRequestByUser,
    onClickDeliveryRequestByNumberId,
  };
};
